import { supabase } from "@/lib/supabase-client";
import { ApplicationType, ApplicationMetaType, ApplicationDataType } from "@/types/db/applications";

// Helper to extract computed fields from meta_data
function transformApplication(app: any): ApplicationType {
  const appData = app.meta_data?.application_data;
  return {
    ...app,
    applicant_name: appData
      ? [appData.firstName, appData.middleInitial, appData.lastName].filter(Boolean).join(" ")
      : "",
    email: appData?.email || "",
    phone: appData?.phone || "",
  };
}

export const applicationService = {
  async getApplications(): Promise<ApplicationType[]> {
    const { data, error } = await supabase
      .from("applications")
      .select(`id, tenant_id, status, meta_data, created_at`)
      .order("created_at", { ascending: false })
      .limit(9000);

    if (error) throw new Error(error.message);
    return (data || []).map(transformApplication);
  },

  async getApplicationById(id: string): Promise<ApplicationType | null> {
    const { data, error } = await supabase
      .from("applications")
      .select(`id, tenant_id, status, meta_data, created_at, updated_at, created_by, updated_by`)
      .eq("id", id)
      .single();

    if (error) {
      if (error.code === "PGRST116") return null; // Not found
      throw new Error(error.message);
    }
    return transformApplication(data);
  },

  async updateApplication(id: string, patch: Partial<ApplicationType>): Promise<ApplicationType> {
    // Remove computed fields before saving
    const { applicant_name, email, phone, ...dbPatch } = patch;

    const { data, error } = await supabase
      .from("applications")
      .update(dbPatch)
      .eq("id", id)
      .select(`id, tenant_id, status, meta_data, created_at`)
      .single();

    if (error) throw new Error(error.message);
    return transformApplication(data);
  },

  async saveRowPatches(patches: (Partial<ApplicationType> & { id: string })[]): Promise<void> {
    await Promise.all(
      patches.map(async (patch) => {
        // Remove computed fields before saving
        const { id, applicant_name, email, phone, ...updateData } = patch;
        const { error } = await supabase.from("applications").update(updateData).eq("id", id);
        if (error) throw new Error(error.message);
      }),
    );
  },

  async deleteApplicationsByIds(ids: string[]): Promise<void> {
    const { error } = await supabase.from("applications").delete().in("id", ids);

    if (error) throw new Error(error.message);
  },

  async createApplication(data: {
    tenant_id: string;
    status?: ApplicationType["status"];
    meta_data?: ApplicationMetaType;
  }): Promise<ApplicationType> {
    const { data: newApp, error } = await supabase
      .from("applications")
      .insert(data)
      .select(`id, tenant_id, status, meta_data, created_at`)
      .single();

    if (error) throw new Error(error.message);
    return transformApplication(newApp);
  },

  /**
   * Submit an application via the public RPC function.
   * Can be called by anonymous users.
   * Currently hardcoded to RockForce tenant.
   */
  async submitApplication(
    applicationData: ApplicationDataType,
    source: "web" | "mobile" | "kiosk" = "web",
  ): Promise<{ success: boolean; application_id?: string; submitted_at?: string; error?: string }> {
    const { data, error } = await supabase.rpc("submit_application", {
      p_application_data: applicationData,
      p_source: source,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return data as { success: boolean; application_id?: string; submitted_at?: string; error?: string };
  },
};
